import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetFeatureSettings,
  useUpdateFeatureSettings,
  type FeatureSettings,
} from '@/services/api/endpoints/SettingsService';

export function SettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useGetFeatureSettings();
  const update = useUpdateFeatureSettings();

  const [cardTopupEnabled, setCardTopupEnabled] = useState<boolean>(false);
  const [dirty, setDirty] = useState(false);

  // Seed local state once the network state arrives.
  useEffect(() => {
    if (data) {
      setCardTopupEnabled(!!data.cardTopup?.enabled);
      setDirty(false);
    }
  }, [data]);

  const handleSave = async () => {
    const patch: FeatureSettings = { cardTopup: { enabled: cardTopupEnabled } };
    try {
      await update.mutateAsync(patch);
      toast.success(t('settings.saved') || 'Postavke sačuvane');
      setDirty(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Greška pri čuvanju');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('settings.title')}</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t('settings.featuresTitle') || 'Funkcionalnosti'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t('settings.featuresDescription') ||
              'Uključite ili isključite funkcionalnosti aplikacije u realnom vremenu. Promene utiču na mobilnu aplikaciju.'}
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 py-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {t('settings.cardTopupLabel') || 'Dopuna karticom'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.cardTopupDescription') ||
                      'Kada je uključeno, korisnici mogu dopunjavati novčanik karticom (3DS i sačuvane kartice). Isključite dok ne završimo integraciju e-money procesora.'}
                  </p>
                </div>
                <Switch
                  checked={cardTopupEnabled}
                  onCheckedChange={(checked) => {
                    setCardTopupEnabled(checked);
                    setDirty(true);
                  }}
                />
              </div>

              <div className="flex justify-end pt-4 border-t mt-4">
                <Button onClick={handleSave} disabled={!dirty || update.isPending}>
                  {update.isPending
                    ? t('common.saving') || 'Čuvanje…'
                    : t('common.save') || 'Sačuvaj'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
